import React, {
  createContext, useContext, useEffect, useRef, useState, useCallback,
} from 'react';
import { Eye, X, Volume2, VolumeX, Play, Pause } from 'lucide-react';
import { apiFetch } from '../App';

/*
 * Coach system — reusable inline "watch a 20s explainer" affordance.
 *
 * Architecture
 * ─────────────
 *   <CoachProvider>           // mount once near the app root
 *      ...your app...
 *      <CoachEye targetId="create.about" />
 *   </CoachProvider>
 *
 * A single player is rendered by the Provider itself; `<CoachEye>` just
 * asks the shared context to play. This guarantees "only one Coach video
 * playing at a time" — clicking a new eye stops the old and starts fresh.
 *
 * Interaction:
 *   • Click eye → fetch a random clip for its targetId → play.
 *   • Click same eye again OR outside the player OR the × → close & stop.
 *   • Any other media on the page pauses when a Coach clip starts, so we
 *     don't dogpile audio.
 */
const CoachContext = createContext(null);

export function useCoach() {
  const ctx = useContext(CoachContext);
  if (!ctx) throw new Error('useCoach must be used within <CoachProvider>');
  return ctx;
}

export function CoachProvider({ children }) {
  const [state, setState] = useState({
    // Which target's eye is currently active (used to visually mark the
    // eye button that opened the player).
    activeTargetId: null,
    clip: null,
    settings: { display_width_desktop: 400, display_width_mobile: 280 },
    loading: false,
    error: null,
    muted: false,
  });

  // Track the fetch generation so slow responses from a previously-cancelled
  // request can't clobber a fresh play.
  const fetchGenRef = useRef(0);

  // Pause every other <audio>/<video> on the page when Coach opens so we
  // don't stack sound. Not aggressive — we just pause; the user can hit
  // play on those again after closing.
  const pauseOtherMedia = () => {
    document.querySelectorAll('audio, video').forEach((el) => {
      if (el.dataset?.coach === 'true') return;
      try { if (!el.paused) el.pause(); } catch { /* detached element — safe */ }
    });
  };

  const closeCoach = useCallback(() => {
    fetchGenRef.current += 1;
    setState((s) => ({ ...s, activeTargetId: null, clip: null, loading: false, error: null }));
  }, []);

  const playCoach = useCallback(async (targetId) => {
    // Toggle: clicking the same eye that's already active closes the player.
    setState((prev) => {
      if (prev.activeTargetId === targetId) {
        fetchGenRef.current += 1;
        return { ...prev, activeTargetId: null, clip: null, loading: false, error: null };
      }
      return { ...prev, activeTargetId: targetId, clip: null, loading: true, error: null };
    });
    // The above `setState` returns early with the closed state when the
    // click was a toggle-off; abort here in that case to avoid a stray
    // fetch. We detect it by peeking after the state has been queued —
    // simplest correct way is a second dispatch that reads current.
    let shouldFetch = true;
    setState((prev) => {
      if (prev.activeTargetId !== targetId) shouldFetch = false;
      return prev;
    });
    if (!shouldFetch) return;

    pauseOtherMedia();
    const myGen = ++fetchGenRef.current;
    try {
      const { data } = await apiFetch(`/coach/clip?target_button_id=${encodeURIComponent(targetId)}`);
      if (myGen !== fetchGenRef.current) return; // stale response
      setState((s) => ({
        ...s,
        clip: data.clip,
        settings: data.settings || s.settings,
        loading: false,
        error: null,
      }));
    } catch (e) {
      if (myGen !== fetchGenRef.current) return;
      // 404 = no clip configured for that target — hide the player,
      // don't show a scary error. Other errors get logged.
      const msg = e?.response?.status === 404 ? null : (e.message || 'Coach unavailable');
      setState((s) => ({ ...s, clip: null, loading: false, error: msg }));
      if (!msg) closeCoach();
    }
  }, [closeCoach]);

  const setMuted = useCallback((m) => setState((s) => ({ ...s, muted: m })), []);

  const value = { state, playCoach, closeCoach, setMuted };
  return (
    <CoachContext.Provider value={value}>
      {children}
      <CoachPlayer />
    </CoachContext.Provider>
  );
}

/* -----------------------------------------------------------------------
 * <CoachEye targetId="..." /> — the small eye button to drop next to a
 * labeled row. Auto-hides itself if the target has no clips configured
 * (checked lazily on first click; if the /coach/clip call 404s, we mark
 * it as unavailable and the icon fades).
 * -------------------------------------------------------------------- */
const _unavailable = new Set();

export function CoachEye({ targetId, className = '', label = '' }) {
  const { state, playCoach } = useCoach();
  const [unavailable, setUnavailable] = useState(_unavailable.has(targetId));
  const isActive = state.activeTargetId === targetId;

  // Ping the endpoint once to know if there's a clip — silent, doesn't
  // block render. If no clip is configured yet we hide the icon so the
  // UI doesn't advertise a feature that has no content.
  useEffect(() => {
    if (_unavailable.has(targetId)) { setUnavailable(true); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/coach/clip?target_button_id=${encodeURIComponent(targetId)}`);
        if (cancelled) return;
        if (res.status === 404) {
          _unavailable.add(targetId);
          setUnavailable(true);
        }
      } catch { /* network flake — leave the icon visible optimistically */ }
    })();
    return () => { cancelled = true; };
  }, [targetId]);

  if (unavailable) return null;

  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); playCoach(targetId); }}
      title={label ? `Watch: ${label}` : 'Watch a quick explainer'}
      className={`
        inline-flex items-center justify-center rounded-full transition-all
        h-6 w-6 sm:h-7 sm:w-7
        ${isActive
          ? 'bg-emerald-500/30 text-emerald-300 ring-2 ring-emerald-400/50'
          : 'bg-white/5 text-emerald-400/80 hover:bg-emerald-500/15 hover:text-emerald-300'}
        ${className}
      `}
      data-testid={`coach-eye-${targetId}`}
      aria-label={label ? `Watch explainer for ${label}` : 'Watch a quick explainer'}
    >
      <Eye className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
    </button>
  );
}

/* -----------------------------------------------------------------------
 * <CoachPlayer /> — the single shared player. Renders as a floating card
 * on ≥sm breakpoints and as a bottom sheet on smaller viewports. Character
 * clip on top, optional soft-subtitle transcript below, mute + close on
 * the right.
 * -------------------------------------------------------------------- */
function CoachPlayer() {
  const { state, closeCoach, setMuted } = useCoach();
  const videoRef = useRef(null);
  // Local play/pause state — kept in sync via the <video> element's
  // onPlay / onPause events so the icon reflects reality even if the
  // video is paused/played by something other than our button.
  const [paused, setPaused] = useState(false);

  // Close on Escape — expected on desktop.
  useEffect(() => {
    if (!state.activeTargetId) return;
    const onKey = (e) => { if (e.key === 'Escape') closeCoach(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state.activeTargetId, closeCoach]);

  // Reset the paused state whenever a new clip mounts — the <video
  // autoPlay> attribute will start it fresh so we want the icon to
  // read Pause right away, not carry over from a previous close.
  useEffect(() => { setPaused(false); }, [state.clip?.clip_id]);

  const togglePlayPause = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play().catch(() => {}); }
    else { v.pause(); }
  };

  if (!state.activeTargetId) return null;

  const { clip, settings, loading, muted } = state;
  const wDesktop = Math.max(240, Math.min(640, settings.display_width_desktop || 400));
  const wMobile = Math.max(200, Math.min(400, settings.display_width_mobile || 280));

  return (
    <>
      {/* Backdrop — tap outside to close. */}
      <div
        className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-sm"
        onClick={closeCoach}
        data-testid="coach-backdrop"
      />
      {/* Player card. On mobile it's anchored to the bottom (bottom-sheet feel);
          on ≥sm it centers as a floating card. */}
      <div
        className="fixed z-[9999] left-1/2 -translate-x-1/2 bottom-0 sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2 w-full sm:w-auto"
        role="dialog"
        aria-modal="true"
        data-testid="coach-player"
      >
        <div
          className="bg-zinc-900 border-t sm:border border-white/10 sm:rounded-2xl shadow-2xl overflow-hidden mx-auto"
          style={{
            // Responsive width — Tailwind can't accept dynamic px, so we
            // inline it. Two vars so mobile and desktop can independently
            // be tuned via the admin settings sliders.
            width: `min(100vw, var(--coach-w))`,
          }}
        >
          {/* CSS-var driven width so the same DOM works on both mobile and
              desktop without JS branching. */}
          <style>{`
            [data-testid="coach-player"] { --coach-w: ${wMobile}px; }
            @media (min-width: 640px) { [data-testid="coach-player"] { --coach-w: ${wDesktop}px; } }
          `}</style>

          {/* Top bar with controls */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-white/5">
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">
              Coach
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setMuted(!muted)}
                className="p-1.5 rounded hover:bg-white/10 text-zinc-400 hover:text-white transition"
                title={muted ? 'Unmute' : 'Mute'}
                data-testid="coach-mute-toggle"
              >
                {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
              </button>
              <button
                onClick={closeCoach}
                className="p-1.5 rounded hover:bg-white/10 text-zinc-400 hover:text-white transition"
                title="Close"
                data-testid="coach-close-btn"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Video */}
          <div className="relative bg-black aspect-square">
            {loading || !clip ? (
              <div className="absolute inset-0 flex items-center justify-center text-zinc-500 text-sm">
                Loading…
              </div>
            ) : (
              <video
                ref={videoRef}
                key={clip.clip_id}
                src={clip.video_url}
                autoPlay
                playsInline
                data-coach="true"
                muted={muted || clip.audio_enabled === false}
                onPlay={() => setPaused(false)}
                onPause={() => setPaused(true)}
                onEnded={closeCoach}
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="coach-video"
              />
            )}
          </div>

          {/* Optional transcript overlay/underlay — shown below the video,
              not baked on top, so it's readable on small screens. */}
          {clip?.transcript && (
            <div className="px-3 py-2 text-xs text-zinc-300 max-h-24 overflow-y-auto bg-black/40" data-testid="coach-transcript">
              {clip.transcript}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
